import React from 'react';
import {BrowserRouter as Router, Route, Routes} from "react-router-dom";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {csCZ} from '@mui/material/locale';
import {AuthProvider} from "./utils/hooks/useAuth";
import {NotificationProvider} from "./utils/hooks/useNotification";
import {ConfirmProvider} from "material-ui-confirm";
import NotFound from "./pages/errors/NotFound";
import Unauthorized from "./pages/errors/Unauthorized";
import Forbidden from "./pages/errors/Forbidden";
import PublicRoute from "./utils/route/PublicRoute";
import PrivateRoute from "./utils/route/PrivateRoute";
import RoleBasedRoute from "./utils/route/RoleBasedRoute";
import UserRole from "./schemas/user/roles";
import MainLayout from "./layouts/MainLayout";
import Login from "./pages/auth/Login";
import RacesOverview from "./pages/races/RacesOverview";
import Races from "./pages/races/Races";
import RaceCreate from "./pages/races/RaceCreate";
import RaceDetails from "./pages/races/RaceDetails";
import RaceOrganize from "./pages/races/RaceOrganize";
import RaceProcessing from "./pages/races/RaceProcessing";
import Results from "./pages/results/Results";
import Boats from "./pages/boats/Boats";
import BoatDetails from "./pages/boats/BoatDetails";
import BoatCreate from "./pages/boats/BoatCreate";
import Clubs from "./pages/clubs/Clubs";
import ClubCreate from "./pages/clubs/ClubCreate";
import ClubDetails from "./pages/clubs/ClubDetails";
import Categories from "./pages/categories/Categories";
import CategoryCreate from "./pages/categories/CategoryCreate";
import CategoryDetails from "./pages/categories/CategoryDetails";
import SetPassword from "./pages/auth/SetPassword";
import ResetPasswordRequest from "./pages/auth/ResetPasswordRequest";
import UserProfile from "./pages/users/UserProfile";
import Users from "./pages/users/Users";
import UserCreate from "./pages/users/UserCreate";
import UserDetails from "./pages/users/UserDetails";
import Seasons from "./pages/seasons/Seasons";
import SeasonDetails from "./pages/seasons/SeasonDetails";
import SeasonCreate from "./pages/seasons/SeasonCreate";

const theme = createTheme(
    {
        palette: {
            primary: {main: '#000080'},
        },
    },
    csCZ
);

function App() {
    return (
        <AuthProvider>
            <NotificationProvider>
                <ConfirmProvider>
                    <ThemeProvider theme={theme}>
                        <Router>
                            <Routes>
                                <Route path="/" element={<MainLayout/>}>
                                    <Route element={<PublicRoute/>}>
                                        <Route index element={<RacesOverview/>}/>
                                        <Route path="results" element={<Results/>}/>
                                        <Route path="boats">
                                            <Route index element={<Boats/>}/>
                                            <Route path=":boatId" element={<BoatDetails/>}/>
                                        </Route>
                                        <Route path="categories">
                                            <Route index element={<Categories/>}/>
                                            <Route path=":categoryId" element={<CategoryDetails/>}/>
                                        </Route>
                                        <Route path="clubs">
                                            <Route index element={<Clubs/>}/>
                                            <Route path=":clubId" element={<ClubDetails/>}/>
                                        </Route>
                                        <Route path="set-password" element={<SetPassword/>}/>
                                        <Route path="reset-password" element={<SetPassword reset/>}/>
                                        <Route path="reset-password-request" element={<ResetPasswordRequest/>}/>
                                        <Route path="unauthorized" element={<Unauthorized/>}/>
                                        <Route path="forbidden" element={<Forbidden/>}/>
                                        <Route path="*" element={<NotFound/>}/>
                                    </Route>
                                    <Route element={<PublicRoute restricted/>}>
                                        <Route path="login" element={<Login/>}/>
                                    </Route>
                                    <Route element={<PrivateRoute/>}>
                                        <Route path="profile" element={<UserProfile/>}/>
                                        <Route path="users">
                                            <Route index element={<Users/>}/>
                                            <Route path=":userId" element={<UserDetails/>}/>
                                        </Route>
                                    </Route>
                                    <Route element={<RoleBasedRoute allowedRoles={[UserRole.ADMIN]}/>}>
                                        <Route path="users/create" element={<UserCreate/>}/>
                                    </Route>
                                    <Route element={<RoleBasedRoute allowedRoles={[UserRole.COMMODORE]}/>}>
                                        <Route path="categories/create" element={<CategoryCreate/>}/>
                                        <Route path="clubs/create" element={<ClubCreate/>}/>
                                        <Route path="seasons">
                                            <Route index element={<Seasons/>}/>
                                            <Route path=":seasonId" element={<SeasonDetails/>}/>
                                            <Route path="create" element={<SeasonCreate/>}/>
                                        </Route>
                                        <Route path="races">
                                            <Route index element={<Races/>}/>
                                            <Route path="create" element={<RaceCreate/>}/>
                                            <Route path=":raceId" element={<RaceDetails/>}/>
                                        </Route>
                                    </Route>
                                    <Route element={<RoleBasedRoute allowedRoles={[UserRole.ORGANIZER]}/>}>
                                        <Route path="boats/create" element={<BoatCreate/>}/>
                                        <Route path="races/organize">
                                            <Route index element={<RaceOrganize/>}/>
                                            <Route path=":raceId" element={<RaceProcessing/>}/>
                                        </Route>
                                    </Route>
                                </Route>
                            </Routes>
                        </Router>
                    </ThemeProvider>
                </ConfirmProvider>
            </NotificationProvider>
        </AuthProvider>
    )
}

export default App;
